import React from "react";
import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";

export const SELF_CLAIM_STEPS = ["Incident", "Documents", "Declaration"];
export const REPRESENTATIVE_CLAIM_STEPS = [
  "Your details",
  "Incident",
  "Documents",
  "Declaration",
];

/**
 * Horizontal pill stepper (used on mobile / small screens).
 *
 * @param {string[]} steps        - Step labels
 * @param {number}   currentIndex - 0-based active step index
 * @param {Function} [onStepClick] - Called with the clicked index.
 *                                   Only fired for completed steps (idx < currentIndex).
 */
const ClaimStepper = ({ steps, currentIndex, onStepClick }) => {
  const lastIndex = Math.max(steps.length - 1, 1);
  const progress  = (currentIndex / lastIndex) * 100;

  return (
    <motion.div
      className="p-4 bg-white rounded-2xl border border-gray-100 shadow-sm"
      initial={{ scale: 1.05, opacity: 0.8 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      <div className="flex gap-2 justify-between items-center">
        {steps.map((label, idx) => {
          const done    = idx < currentIndex;
          const active  = idx === currentIndex;
          const canClick = done && !!onStepClick;

          return (
            <motion.div
              key={label}
              className="flex gap-2 items-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: idx * 0.1, duration: 0.3 }}
            >
              <motion.button
                type="button"
                disabled={!canClick}
                onClick={() => canClick && onStepClick(idx)}
                className={`grid h-8 w-8 place-items-center rounded-full text-xs font-semibold transition-all ${
                  done || active
                    ? "text-white bg-emerald-600"
                    : "text-gray-500 bg-gray-100"
                } ${canClick ? "cursor-pointer hover:ring-2 hover:ring-emerald-300 hover:scale-105" : "cursor-default"}`}
                animate={{
                  scale: active ? 1.1 : 1,
                  backgroundColor: done || active ? "#059669" : "#f3f4f6",
                }}
                transition={{ duration: 0.2 }}
                title={canClick ? `Go to ${label}` : undefined}
              >
                {done ? <CheckCircle2 size={14} /> : idx + 1}
              </motion.button>

              <motion.span
                className={`hidden sm:block text-xs ${
                  done || active ? "text-gray-900" : "text-gray-500"
                } ${canClick ? "cursor-pointer hover:text-emerald-700" : ""}`}
                onClick={() => canClick && onStepClick(idx)}
                animate={{
                  color:      done || active ? "#111827" : "#6b7280",
                  fontWeight: active ? 600 : done ? 500 : 400,
                }}
                transition={{ duration: 0.2 }}
              >
                {label}
              </motion.span>

              {idx < steps.length - 1 && (
                <motion.div
                  className="w-6 h-px bg-gray-200"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ delay: idx * 0.1 + 0.2, duration: 0.3 }}
                />
              )}
            </motion.div>
          );
        })}
      </div>

      <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
        <motion.div
          className="h-full bg-gradient-to-r from-emerald-500 to-green-600"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        />
      </div>
    </motion.div>
  );
};

export default ClaimStepper;
